import axios from 'axios';
import { getCached, setCached, deduplicateRequest, clearCache, clearAllCache } from '../utils/apiCache';

// Use relative path for Vercel serverless deployment (backend and frontend on same domain)
// Falls back to localhost for local development
const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000');
const SITE_ID = process.env.REACT_APP_SITE_ID || '00000000-0000-0000-0000-000000000000';

// Create axios instance with timeout
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Export api instance for direct use
export { api };

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
    }
    return response.data;
  },
  logout: () => {
    localStorage.removeItem('token');
  },
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Content Sections API
export const contentAPI = {
  getSections: async (pageName, options = {}) => {
    const includeInactive = options.includeInactive === true;
    const cacheKey = `sections-${pageName}${includeInactive ? '-all' : ''}`;
    
    // Check cache first
    const cached = getCached(cacheKey);
    if (cached !== null) {
      return cached;
    }
    
    // Deduplicate requests
    return deduplicateRequest(cacheKey, async () => {
      try {
        const params = includeInactive ? { include_inactive: true } : {};
        const response = await api.get(`/content/sections/${pageName}`, { params });
        const data = response.data;
        setCached(cacheKey, data);
        return data;
      } catch (error) {
        // Return cached data if available, even if expired
        const staleCache = getCached(cacheKey);
        if (staleCache !== null) {
          console.warn('API call failed, using stale cache:', error);
          return staleCache;
        }
        throw error;
      }
    });
  },
  createSection: async (section) => {
    const response = await api.post('/content/sections', section);
    // Clear cache for the page
    const pageName = section.page_name || "home"; // Default to home if not specified
    clearCache(`sections-${pageName}`);
    // Also clear all section caches to ensure fresh data
    clearAllCache(); // Clear all caches
    return response.data;
  },
  updateSection: async (sectionId, section) => {
    const response = await api.put(`/content/sections/${sectionId}`, section);
    // Clear cache for the page (if page_name is in section or response)
    const pageName = section.page_name || "home"; // Default to home if not specified
    clearCache(`sections-${pageName}`);
    // Also clear all section caches to ensure fresh data
    clearAllCache(); // Clear all caches
    return response.data;
  },
  deleteSection: async (sectionId) => {
    const response = await api.delete(`/content/sections/${sectionId}`);
    // Note: Cache will expire naturally, or clear all section caches for safety
    return response.data;
  },
  reorderSections: async (sections) => {
    const response = await api.put('/content/sections/reorder', { sections });
    clearAllCache();
    return response.data;
  },
};

// Case Studies API
export const caseStudiesAPI = {
  getAll: async () => {
    const cacheKey = 'case-studies-all';
    const cached = getCached(cacheKey);
    if (cached !== null) return cached;
    
    return deduplicateRequest(cacheKey, async () => {
      try {
        const response = await api.get('/case-studies/');
        const data = response.data;
        setCached(cacheKey, data);
        return data;
      } catch (error) {
        const staleCache = getCached(cacheKey);
        if (staleCache !== null) {
          console.warn('API call failed, using stale cache:', error);
          return staleCache;
        }
        throw error;
      }
    });
  },
  create: async (caseStudy) => {
    const response = await api.post('/case-studies/', caseStudy);
    return response.data;
  },
  update: async (id, caseStudy) => {
    const response = await api.put(`/case-studies/${id}`, caseStudy);
    // Clear cache after update
    clearCache('case-studies-all');
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/case-studies/${id}`);
    // Clear cache after delete
    clearCache('case-studies-all');
    return response.data;
  },
};

// Testimonials API
export const testimonialsAPI = {
  getAll: async () => {
    const cacheKey = 'testimonials-all';
    const cached = getCached(cacheKey);
    if (cached !== null) return cached;
    
    return deduplicateRequest(cacheKey, async () => {
      try {
        const response = await api.get('/testimonials/');
        const data = response.data;
        setCached(cacheKey, data);
        return data;
      } catch (error) {
        const staleCache = getCached(cacheKey);
        if (staleCache !== null) {
          console.warn('API call failed, using stale cache:', error);
          return staleCache;
        }
        throw error;
      }
    });
  },
  create: async (testimonial) => {
    const response = await api.post('/testimonials/', testimonial);
    clearCache('testimonials-all');
    return response.data;
  },
  update: async (id, testimonial) => {
    const response = await api.put(`/testimonials/${id}`, testimonial);
    clearCache('testimonials-all');
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/testimonials/${id}`);
    clearCache('testimonials-all');
    return response.data;
  },
};

// Services API
export const servicesAPI = {
  getAll: async () => {
    const cacheKey = 'services-all';
    const cached = getCached(cacheKey);
    if (cached !== null) return cached;
    
    return deduplicateRequest(cacheKey, async () => {
      try {
        const response = await api.get('/services/');
        const data = response.data;
        setCached(cacheKey, data);
        return data;
      } catch (error) {
        const staleCache = getCached(cacheKey);
        if (staleCache !== null) {
          console.warn('API call failed, using stale cache:', error);
          return staleCache;
        }
        throw error;
      }
    });
  },
  create: async (service) => {
    const response = await api.post('/services/', service);
    clearCache('services-all');
    return response.data;
  },
  update: async (id, service) => {
    const response = await api.put(`/services/${id}`, service);
    clearCache('services-all');
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/services/${id}`);
    clearCache('services-all');
    return response.data;
  },
};

// Pricing Plans API
export const pricingPlansAPI = {
  getAll: async () => {
    const cacheKey = 'pricing-plans-all';
    const cached = getCached(cacheKey);
    if (cached !== null) return cached;
    
    return deduplicateRequest(cacheKey, async () => {
      try {
        const response = await api.get('/pricing-plans/');
        const data = response.data;
        setCached(cacheKey, data);
        return data;
      } catch (error) {
        const staleCache = getCached(cacheKey);
        if (staleCache !== null) {
          console.warn('API call failed, using stale cache:', error);
          return staleCache;
        }
        throw error;
      }
    });
  },
  create: async (plan) => {
    const response = await api.post('/pricing-plans/', plan);
    clearCache('pricing-plans-all');
    return response.data;
  },
  update: async (id, plan) => {
    const response = await api.put(`/pricing-plans/${id}`, plan);
    clearCache('pricing-plans-all');
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/pricing-plans/${id}`);
    clearCache('pricing-plans-all');
    return response.data;
  },
};

// Team Members API
export const teamAPI = {
  getAll: async () => {
    const cacheKey = 'team-all';
    const cached = getCached(cacheKey);
    if (cached !== null) return cached;
    
    return deduplicateRequest(cacheKey, async () => {
      try {
        const response = await api.get('/team/');
        const data = response.data;
        setCached(cacheKey, data);
        return data;
      } catch (error) {
        const staleCache = getCached(cacheKey);
        if (staleCache !== null) {
          console.warn('API call failed, using stale cache:', error);
          return staleCache;
        }
        throw error;
      }
    });
  },
  create: async (member) => {
    const response = await api.post('/team/', member);
    clearCache('team-all');
    return response.data;
  },
  update: async (id, member) => {
    const response = await api.put(`/team/${id}`, member);
    clearCache('team-all');
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/team/${id}`);
    clearCache('team-all');
    return response.data;
  },
};


// Contact API
export const contactAPI = {
  submit: async (submission) => {
    const response = await api.post('/contact/submit', submission);
    return response.data;
  },
  getSubmissions: async () => {
    const response = await api.get('/contact/submissions');
    return response.data;
  },
  updateSubmission: async (id, submission) => {
    const response = await api.put(`/contact/submissions/${id}`, submission);
    return response.data;
  },
};

// File Upload API - Vercel Blob Only
export const filesAPI = {
  upload: async (file, fileType = null, onProgress = null) => {
    // Vercel Blob token is required
    const blobToken = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    
    if (!blobToken) {
      throw new Error('Vercel Blob token not configured. Please set REACT_APP_BLOB_READ_WRITE_TOKEN environment variable.');
    }
    
    // Upload to Vercel Blob
    const { put } = await import('@vercel/blob');
    
    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const uniqueFilename = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
    
    // Show progress immediately
    if (onProgress) {
      onProgress(10); // Start
    }
    
    // Upload to Vercel Blob
    const blob = await put(uniqueFilename, file, {
      access: 'public',
      token: blobToken,
    });
    
    // Complete progress
    if (onProgress) {
      onProgress(100);
    }
    
    console.log('✅ File uploaded to Vercel Blob:', blob.url);
    
    // Store metadata in backend database
    try {
      await api.post('/files/metadata', {
        filename: uniqueFilename,
        original_filename: file.name,
        file_url: blob.url,
        file_type: fileType || (file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'document'),
        mime_type: file.type,
        file_size: file.size,
        storage_type: 'vercel_blob'
      });
    } catch (metadataError) {
      console.warn('Failed to save file metadata to backend:', metadataError);
      // Continue anyway - file is uploaded, metadata is optional
    }
    
    return {
      id: uniqueFilename,
      filename: uniqueFilename,
      original_filename: file.name,
      file_path: blob.url,
      file_type: fileType || (file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'document'),
      mime_type: file.type,
      file_size: file.size,
      url: blob.url, // Direct CDN URL
      created_at: new Date().toISOString()
    };
  },
  getFileUrl: (fileId) => {
    // If it's already a full URL (Vercel Blob), return as-is
    if (fileId && (fileId.startsWith('http://') || fileId.startsWith('https://'))) {
      return fileId;
    }
    // Otherwise, assume it's a Vercel Blob URL format
    return fileId;
  },
  delete: async (fileId) => {
    // Delete from Vercel Blob
    const blobToken = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    if (!blobToken) {
      throw new Error('Vercel Blob token not configured');
    }
    
    if (fileId.includes('blob.vercel-storage.com')) {
      try {
        const { del } = await import('@vercel/blob');
        await del(fileId, { token: blobToken });
      } catch (error) {
        console.error('Failed to delete from Vercel Blob:', error);
        throw error;
      }
    }
    
    // Also delete metadata from backend
    try {
      await api.delete(`/files/${fileId}`);
    } catch (error) {
      console.error('Failed to delete file metadata:', error);
    }
    
    return { message: 'File deleted successfully' };
  },
};

// Google Reviews API
export const reviewsAPI = {
  getVisible: async (minRating = 1) => {
    const response = await api.get(`/reviews/?min_rating=${minRating}`);
    return response.data;
  },
  getMeta: async () => {
    const response = await api.get('/reviews/meta');
    return response.data;
  },
  getAllAdmin: async () => {
    const response = await api.get('/reviews/all');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/reviews/', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/reviews/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/reviews/${id}`);
    return response.data;
  },
  sync: async () => {
    const response = await api.post('/reviews/sync');
    return response.data;
  },
};

export default api;

